import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchUsers, toggleUserActive } from '../../api/admin';
import AddSuperAdminModal from '../../components/admin/AddSuperAdminModal';
import ResetPasswordModal from '../../components/admin/ResetPasswordModal';
import StatusBadge from '../../components/admin/StatusBadge';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import SearchAutocomplete from '../../components/ui/SearchAutocomplete';
import { useDebounce } from '../../hooks/useDebounce';
import { useToast } from '../../context/ToastContext';

const ROLES = ['', 'SUPER_ADMIN', 'SALON_MANAGER', 'SALON_STAFF', 'CLIENT'];

const roleBadgeClass = {
  SUPER_ADMIN: 'bg-purple-500/20 text-purple-300',
  SALON_MANAGER: 'bg-glow-600/20 text-glow-300',
  SALON_STAFF: 'bg-blue-500/20 text-blue-300',
  CLIENT: 'bg-zinc-600/20 text-zinc-700 dark:text-zinc-300',
};

const AdminUsersPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [role, setRole] = useState('');
  const [isActive, setIsActive] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const debouncedInput = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [resetUser, setResetUser] = useState(null);
  const [addSuperAdminOpen, setAddSuperAdminOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', role, isActive, appliedSearch, page, pageSize],
    queryFn: () =>
      fetchUsers({
        role: role || undefined,
        isActive: isActive || undefined,
        search: appliedSearch || undefined,
        page,
        limit: pageSize,
      }),
  });

  const { data: suggestionData, isFetching: suggestionsLoading } = useQuery({
    queryKey: ['admin-user-suggestions', debouncedInput, role, isActive],
    queryFn: () =>
      fetchUsers({
        search: debouncedInput,
        role: role || undefined,
        isActive: isActive || undefined,
        page: 1,
        limit: 8,
      }),
    enabled: debouncedInput.trim().length >= 1,
  });

  const suggestions = (suggestionData?.data || []).map((u) => ({
    label: `${u.firstName} ${u.lastName}`.trim(),
    value: `${u.firstName} ${u.lastName}`.trim(),
    hint: u.email,
  }));

  const toggleMut = useMutation({
    mutationFn: toggleUserActive,
    onSuccess: (res) => {
      showToast(
        res.user.isActive ? t('toast.userReactivated') : t('toast.userDeactivated'),
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  return (
    <DashboardShell title={t('admin.users.title')} subtitle={t('admin.users.platformSubtitle')}>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <select
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
        >
          {ROLES.map((r) => (
            <option key={r || 'all'} value={r}>
              {r ? t(`roles.${r}`) : t('admin.users.allRoles')}
            </option>
          ))}
        </select>
        <select
          value={isActive}
          onChange={(e) => {
            setIsActive(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
        >
          <option value="">{t('admin.users.allStatuses')}</option>
          <option value="true">{t('common.active')}</option>
          <option value="false">{t('common.inactive')}</option>
        </select>
        <SearchAutocomplete
          className="min-w-[220px] flex-1"
          value={searchInput}
          onChange={setSearchInput}
          onSearch={(v) => {
            const next =
              v && typeof v === 'object'
                ? String(v.value ?? v.label ?? '').trim()
                : String(v ?? '').trim();
            setAppliedSearch(next);
            setSearchInput(next);
            setPage(1);
          }}
          suggestions={suggestions}
          loading={suggestionsLoading}
          placeholder={t('admin.users.searchNameOrEmail')}
        />
        <button
          type="button"
          onClick={() => setAddSuperAdminOpen(true)}
          className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white hover:bg-glow-500"
        >
          {t('admin.users.addSuperAdmin')}
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-zinc-200 bg-white text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-500">
            <tr>
              <th className="px-4 py-3">{t('admin.users.user')}</th>
              <th className="px-4 py-3">{t('common.email')}</th>
              <th className="px-4 py-3">{t('admin.users.role')}</th>
              <th className="px-4 py-3">{t('admin.users.tenant')}</th>
              <th className="px-4 py-3">{t('common.status')}</th>
              <th className="px-4 py-3">{t('admin.users.lastLogin')}</th>
              <th className="px-4 py-3">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-zinc-600 dark:text-zinc-500">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {(data?.data || []).map((u) => (
              <tr key={u._id} className="border-b border-zinc-200/50 dark:border-zinc-800/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {u.profilePicture ? (
                      <img src={u.profilePicture} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-xs dark:bg-zinc-800">
                        {u.firstName[0]}
                      </div>
                    )}
                    <span>
                      {u.firstName} {u.lastName}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${roleBadgeClass[u.role]}`}>
                    {t(`roles.${u.role}`)}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                  {u.tenantId?.businessName || '—'}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={u.isActive ? 'ACTIVE' : 'CANCELLED'} />
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    {u.role !== 'SUPER_ADMIN' && (
                      <button
                        type="button"
                        onClick={() => toggleMut.mutate(u._id)}
                        className="text-left text-xs text-glow-400 hover:underline"
                      >
                        {u.isActive ? t('admin.users.deactivate') : t('admin.users.reactivate')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setResetUser(u)}
                      className="text-left text-xs text-amber-500 hover:underline"
                    >
                      {t('admin.users.resetPassword')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddSuperAdminModal
        open={addSuperAdminOpen}
        onClose={() => setAddSuperAdminOpen(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['admin-users'] })}
      />
      <ResetPasswordModal user={resetUser} open={!!resetUser} onClose={() => setResetUser(null)} />

      <PaginatedFooter
        pagination={data?.pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </DashboardShell>
  );
};

export default AdminUsersPage;
